/* eslint-disable */
/**
 * SchedulerPanel.jsx — Planlagte OS-jobs (erstatter legacy Scheduler-fane)
 *
 * Datakilde (verificeret):
 *   GET  /api/os/planlagte       → { planlagte: [{id, navn, beskrivelse, cron_udtryk,
 *                                      afdeling, aktiv, sidst_koert_kl, naeste_koert_kl}] }
 *   PATCH /api/os/planlagte/:id  → { aktiv, koer_nu } — verificeret i os.js linje 477
 *
 * Design: Sager-stil med KpiKort-rad, DataTabel, stale-advarsel og kortliste på mobil.
 * Mobil-først: Android 360px viewport.
 */

import React, { useState, useCallback } from 'react';
import { Clock, Play, CheckCircle2, AlertCircle, Activity, RefreshCw } from 'lucide-react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  SektionsHeader,
  SammenklappeligSektion,
  useApiPolling,
  useWindowWidth,
  BG, KANT, TEKST, STATUS, RADIUS, SPACING, TYPE, FONT,
} from '../komponenter/index';

// ─── Hjælpefunktioner ────────────────────────────────────────────────────────

function formatTid(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function næsteLabel(iso) {
  if (!iso) return '—';
  const diff = new Date(iso) - Date.now();
  if (diff < 0) {
    const minSiden = Math.round(-diff / 60000);
    if (minSiden < 60) return `${minSiden} min overskredet`;
    return `${Math.round(minSiden / 60)} timer overskredet`;
  }
  const minFrem = Math.round(diff / 60000);
  if (minFrem < 60) return `om ${minFrem} min`;
  if (minFrem < 1440) return `om ${Math.round(minFrem / 60)} timer`;
  return `om ${Math.round(minFrem / 1440)} dage`;
}

function erStale(naeste_koert_kl) {
  if (!naeste_koert_kl) return false;
  return new Date(naeste_koert_kl) < new Date(Date.now() - 60 * 60 * 1000); // >60 min i fortiden
}

// ─── Mobil: job-kortliste ─────────────────────────────────────────────────────

function JobKortListe({ jobs, onKoerNu, koerNuId }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {jobs.map((job) => {
        const aktiv = !!job.aktiv;
        const stale = aktiv && erStale(job.naeste_koert_kl);
        const kantFarve = aktiv ? (stale ? STATUS.opmærksomhed : STATUS.live) : STATUS.ikkeForbundet;
        return (
          <div
            key={job.id}
            style={{
              background: BG.kort,
              border: `1px solid ${KANT.standard}`,
              borderLeft: `3px solid ${kantFarve}`,
              borderRadius: RADIUS.kort,
              padding: '12px 14px',
              fontFamily: FONT.family,
            }}
          >
            {/* Navn + status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: TEKST.primær, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {job.navn}
              </span>
              <StatusBadge
                status={aktiv ? (stale ? 'OPMÆRKSOMHED' : 'LIVE') : 'IKKE FORBUNDET'}
                label={aktiv ? (stale ? 'STALE' : 'AKTIV') : 'INAKTIV'}
              />
            </div>

            {/* Cron + afdeling */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ ...TYPE.mono, color: TEKST.sekundær, fontSize: 11 }}>
                {job.cron_udtryk || '—'}
              </span>
              <span style={{ fontSize: 11, color: TEKST.sekundær }}>{job.afdeling || '—'}</span>
            </div>

            {/* Sidst/næste + knap */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 11, color: TEKST.sekundær, lineHeight: 1.6 }}>
                <div>Sidst: {formatTid(job.sidst_koert_kl)}</div>
                <div style={{ color: stale ? STATUS.opmærksomhed : TEKST.sekundær }}>
                  Næste: {næsteLabel(job.naeste_koert_kl)}
                </div>
              </div>
              <button
                onClick={() => onKoerNu(job.id)}
                disabled={koerNuId === job.id}
                style={{
                  background: koerNuId === job.id ? 'rgba(63,212,199,0.08)' : 'rgba(63,212,199,0.12)',
                  border: `1px solid rgba(63,212,199,0.3)`,
                  borderRadius: RADIUS.knap,
                  color: '#3fd4c7',
                  fontSize: 11, fontWeight: 700,
                  padding: '5px 10px',
                  cursor: koerNuId === job.id ? 'not-allowed' : 'pointer',
                  opacity: koerNuId === job.id ? 0.5 : 1,
                  minHeight: 36,
                }}
              >
                {koerNuId === job.id ? '...' : 'Kør nu'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────────────────────

export default function SchedulerPanel() {
  const bredde = useWindowWidth();
  const isMobil = bredde < 600;

  const [koerNuId, setKoerNuId] = useState(null);
  const [koerFejl, setKoerFejl] = useState(null);

  // Data polling — 30s interval
  const { data, fejl, indlæser } = useApiPolling('/os/planlagte', 30000);

  const jobs = data?.planlagte || [];

  // KPI-beregninger
  const aktiveJobs    = jobs.filter(j => j.aktiv).length;
  const inaktiveJobs  = jobs.filter(j => !j.aktiv).length;
  const senstKørt     = jobs
    .filter(j => j.sidst_koert_kl)
    .sort((a, b) => new Date(b.sidst_koert_kl) - new Date(a.sidst_koert_kl))[0];
  const næsteKørsel   = jobs
    .filter(j => j.aktiv && j.naeste_koert_kl)
    .sort((a, b) => new Date(a.naeste_koert_kl) - new Date(b.naeste_koert_kl))[0];

  // Stale-advarsel: er nogen aktive jobs "stale" (naeste_koert_kl > 60 min i fortiden)?
  const staleJobs = jobs.filter(j => j.aktiv && erStale(j.naeste_koert_kl));
  const harStale  = staleJobs.length > 0;

  // "Kør nu" handling
  const handleKoerNu = useCallback(async (id) => {
    setKoerNuId(id);
    setKoerFejl(null);
    try {
      const res = await fetch(`/api/os/planlagte/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ koer_nu: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      setKoerFejl(`Kunne ikke køre job: ${err.message}`);
    } finally {
      setKoerNuId(null);
    }
  }, []);

  // DataTabel kolonner
  const kolonner = [
    {
      nøgle: 'navn',
      label: 'Job',
      render: (v, row) => (
        <div>
          <div style={{ ...TYPE.tabelCelle, color: TEKST.primær, fontWeight: 600 }}>{v}</div>
          {row.beskrivelse && (
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginTop: 2, lineHeight: 1.4 }}>
              {row.beskrivelse}
            </div>
          )}
        </div>
      ),
    },
    {
      nøgle: 'afdeling',
      label: 'Afdeling',
      render: (v) => (
        <span style={{ fontSize: 11, color: TEKST.sekundær }}>{v || '—'}</span>
      ),
    },
    {
      nøgle: 'cron_udtryk',
      label: 'Cron',
      render: (v) => (
        <span style={{ ...TYPE.mono, color: TEKST.sekundær }}>{v || '—'}</span>
      ),
    },
    {
      nøgle: 'sidst_koert_kl',
      label: 'Sidst kørt',
      render: (v) => (
        <span style={{ fontSize: 12, color: TEKST.sekundær }}>{formatTid(v)}</span>
      ),
    },
    {
      nøgle: 'naeste_koert_kl',
      label: 'Næste kørsel',
      render: (v, row) => {
        const stale = row.aktiv && erStale(v);
        return (
          <span style={{ fontSize: 12, color: stale ? STATUS.opmærksomhed : TEKST.sekundær }}>
            {næsteLabel(v)}
          </span>
        );
      },
    },
    {
      nøgle: 'aktiv',
      label: 'Status',
      render: (v, row) => {
        const stale = v && erStale(row.naeste_koert_kl);
        return (
          <StatusBadge
            status={v ? (stale ? 'OPMÆRKSOMHED' : 'LIVE') : 'IKKE FORBUNDET'}
            label={v ? (stale ? 'STALE' : 'AKTIV') : 'INAKTIV'}
          />
        );
      },
    },
    {
      nøgle: 'id',
      label: 'Handling',
      render: (v) => (
        <button
          onClick={() => handleKoerNu(v)}
          disabled={koerNuId === v}
          style={{
            background: koerNuId === v ? 'rgba(63,212,199,0.05)' : 'rgba(63,212,199,0.1)',
            border: '1px solid rgba(63,212,199,0.25)',
            borderRadius: RADIUS.knap,
            color: '#3fd4c7',
            fontSize: 11, fontWeight: 700,
            padding: '4px 10px',
            cursor: koerNuId === v ? 'not-allowed' : 'pointer',
            opacity: koerNuId === v ? 0.5 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {koerNuId === v ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} />
              Sender…
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Play size={11} />
              Kør nu
            </span>
          )}
        </button>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: FONT.family }}>

      {/* Header */}
      <SektionsHeader
        titel="Scheduler"
        undertitel="Planlagte OS-jobs og automatiske kørersler"
        ikon={<Activity size={16} />}
        badge={indlæser ? 'KONFIGURERET' : fejl ? 'FEJL' : 'LIVE'}
      />

      {/* Stale-advarsel */}
      {harStale && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderLeft: `3px solid ${STATUS.opmærksomhed}`,
          borderRadius: RADIUS.kort,
          padding: '12px 16px',
          marginBottom: SPACING.gap,
          fontSize: 13,
        }}>
          <AlertCircle size={16} color={STATUS.opmærksomhed} style={{ flexShrink: 0 }} />
          <div>
            <span style={{ fontWeight: 700, color: STATUS.opmærksomhed }}>Kørsel muligvis stale</span>
            <span style={{ color: TEKST.sekundær, marginLeft: 8 }}>
              {staleJobs.length} aktiv{staleJobs.length > 1 ? 'e' : 't'} job{staleJobs.length > 1 ? 's' : ''} har overskredet næste kørsel med mere end 60 minutter.
            </span>
          </div>
          <StatusBadge status="OPMÆRKSOMHED" label="STALE" style={{ marginLeft: 'auto', flexShrink: 0 }} />
        </div>
      )}

      {/* Fejlbesked fra "Kør nu" */}
      {koerFejl && (
        <div style={{
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: RADIUS.kort, padding: '10px 14px', marginBottom: SPACING.gap,
          fontSize: 13, color: STATUS.fejl,
        }}>
          {koerFejl}
        </div>
      )}

      {/* KPI-rad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobil ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: SPACING.gap,
        marginBottom: SPACING.sektionGap,
      }}>
        <KpiKort
          label="Aktive jobs"
          kortLabel="AKTIVE"
          value={jobs.length === 0 && !indlæser ? '—' : aktiveJobs}
          farve={STATUS.live}
          ikon={<CheckCircle2 size={16} />}
          badge="LIVE"
          mobil={isMobil}
        />
        <KpiKort
          label="Inaktive jobs"
          kortLabel="INAKTIVE"
          value={jobs.length === 0 && !indlæser ? '—' : inaktiveJobs}
          farve={STATUS.ikkeForbundet}
          ikon={<Clock size={16} />}
          badge="LIVE"
          mobil={isMobil}
        />
        <KpiKort
          label="Sidst kørt"
          kortLabel="SIDST"
          value={senstKørt ? formatTid(senstKørt.sidst_koert_kl) : '—'}
          farve={STATUS.konfigureret}
          ikon={<Activity size={16} />}
          badge={senstKørt ? 'LIVE' : 'IKKE FORBUNDET'}
          mobil={isMobil}
          style={isMobil ? {} : { gridColumn: 'span 1' }}
        />
        <KpiKort
          label="Næste kørsel"
          kortLabel="NÆSTE"
          value={næsteKørsel ? næsteLabel(næsteKørsel.naeste_koert_kl) : '—'}
          farve={harStale ? STATUS.opmærksomhed : STATUS.ai}
          ikon={<Clock size={16} />}
          badge={næsteKørsel ? (harStale ? 'OPMÆRKSOMHED' : 'LIVE') : 'IKKE FORBUNDET'}
          mobil={isMobil}
        />
      </div>

      {/* Job-liste */}
      <SammenklappeligSektion titel="Planlagte jobs" defaultÅben={true} isMobil={isMobil}>
        <div style={{ marginTop: isMobil ? 0 : 0 }}>
          {indlæser && jobs.length === 0 ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
              Henter jobs…
            </div>
          ) : fejl && jobs.length === 0 ? (
            <TomTilstand
              titel="Scheduler ikke tilgængelig"
              besked={`Endpoint /api/os/planlagte returnerede fejl: ${fejl}`}
              ikon={<AlertCircle size={24} />}
              badge="FEJL"
            />
          ) : jobs.length === 0 ? (
            <TomTilstand
              titel="Ingen planlagte jobs"
              besked="Ingen OS-jobs er oprettet i os.planlagte_opgaver endnu."
              ikon={<Clock size={24} />}
              badge="IKKE FORBUNDET"
            />
          ) : isMobil ? (
            <JobKortListe jobs={jobs} onKoerNu={handleKoerNu} koerNuId={koerNuId} />
          ) : (
            <DataTabel
              kolonner={kolonner}
              rækker={jobs}
              tomBesked="Ingen planlagte jobs"
            />
          )}
        </div>
      </SammenklappeligSektion>

    </div>
  );
}
